import { DataScopeOrder } from './types'

export enum DataClassName {
  ACTIVITY = 'Activity',
  ACTIVITY_CONTACT_RELATION = 'ActivityContactRelation',
  ACTIVITY_DOCUMENT_RELATION = 'ActivityDocumentRelation',
  ACTIVITY_FOLLOW_UP_RELATION = 'ActivityFollowUpRelation',
  ACTIVITY_PROJECT_RELATION = 'ActivityProjectRelation',
  ADDRESS = 'Address',
  ADDRESS_CONTACT_RELATION = 'AddressContactRelation',
  COMPANY = 'Company',
  COMPANY_PERSON_RELATION = 'CompanyPersonRelation',
  CONTACT = 'Contact',
  CONTACT_CONTACT_RELATION = 'ContactContactRelation',
  CONTACT_DOCUMENT_RELATION = 'ContactDocumentRelation',
  CONTACT_LIST_RELATION = 'ContactListRelation',
  CONTACT_PROJECT_RELATION = 'ContactProjectRelation',
  DASHBOARD = 'Dashboard',
  DOCUMENT = 'Document',
  DOCUMENT_DOCUMENT_RELATION = 'DocumentDocumentRelation',
  DOCUMENT_PROJECT_RELATION = 'DocumentProjectRelation',
  FILTER = 'Filter',
  FOLLOW_UP = 'FollowUp',
  GLOBAL_RESULT = 'GlobalResult',
  INDUSTRY = 'Industry',
  INVOICE = 'Invoice',
  JOB = 'Job',
  LEAD = 'Lead',
  LIST = 'List',
  MESSAGE = 'Message',
  MY_WORK_CONTACT_RELATION = 'MyWorkContactRelation',
  OPPORTUNITY = 'Opportunity',
  ORDER = 'Order',
  PERSON = 'Person',
  PROJECT = 'Project',
  QUOTE = 'Quote',
  REGION = 'Region',
  SCHEMA = 'Schema',
  SOCIAL_NETWORK = 'SocialNetwork',
  TICKET = 'Ticket',
  TIMERECORD = 'Timerecord',
  TIMERECORD_CLIENT = 'TimerecordClient',
  TIMERECORD_DESCRIPTION = 'TimerecordDescription',
  TIMERECORD_ORDER = 'TimerecordOrder',
  TIMERECORD_POSITION = 'TimerecordPosition',
  TIMERECORD_USER = 'TimerecordUser',
  TIMERECORD_TEAM = 'TimerecordTeam',
  TIMERECORD_BUDGET = 'TimerecordBudget',
  TIMERECORD_UTILIZATION = 'TimerecordUtilization',
  WHOAMI = 'WhoAmI',
}

const DATA_CLASS_NAME_VALUES = new Set<string>(Object.values(DataClassName))

/** Narrows an arbitrary class-name string to a `DataClassName` enum member. */
export const isDataClassName = (value: string): value is DataClassName =>
  DATA_CLASS_NAME_VALUES.has(value)

export enum PisaDataClassEndpoint {
  ACTIVITY = 'activity',
  ACTIVITY_CONTACT = 'activity-contact',
  ACTIVITY_DOCUMENT = 'activity-document',
  ACTIVITY_FOLLOW_UP = 'activity-follow-up',
  ACTIVITY_PROJECT = 'activity-project',
  ADDRESS = 'address',
  ADDRESS_CONTACT = 'address-contact',
  COMPANY = 'company',
  COMPANY_PERSON = 'company-person',
  CONTACT = 'contact',
  CONTACT_CONTACT = 'contact-contact',
  CONTACT_DOCUMENT = 'contact-document',
  CONTACT_LIST = 'contact-list',
  CONTACT_PROJECT = 'contact-project',
  DOCUMENT = 'document',
  DOCUMENT_DOCUMENT = 'document-document',
  DOCUMENT_PROJECT = 'document-project',
  FILTER = 'filter',
  FOLLOW_UP = 'follow-up',
  GLOBAL_RESULT = 'global-result',
  INDUSTRY = 'industry',
  INVOICE = 'invoice',
  JOB = 'job',
  LEAD = 'lead',
  LIST = 'list',
  MESSAGE = 'message',
  MY_WORK_CONTACT = 'my-work-contact',
  OPPORTUNITY = 'opportunity',
  ORDER = 'order',
  PERSON = 'person',
  PROJECT = 'project',
  QUOTE = 'quote',
  REGION = 'region',
  SCHEMA = 'schema',
  SOCIAL_NETWORK = 'social-network',
  TICKET = 'ticket',
  TIMERECORD = 'timerecord',
  TIMERECORD_CLIENT = 'timerecord-client',
  TIMERECORD_DESCRIPTION = 'timerecord-description',
  TIMERECORD_ORDER = 'timerecord-order',
  TIMERECORD_POSITION = 'timerecord-position',
  TIMERECORD_USER = 'timerecord-user',
  TIMERECORD_TEAM = 'timerecord-team',
  TIMERECORD_BUDGET = 'timerecord-budget',
  TIMERECORD_UTILIZATION = 'timerecord-utilization',
  WHOAMI = 'whoami',
}

export const DASHBAORD_ELEMENTS_ENDPOINT = 'cockpit-element'
export const DASHBAORD_DATA_ENDPOINT = 'cockpit-data'
export const DEFAULT_VALUES_ENDPOINT = 'default'

/**
 * All available feature flags, add more when needed.
 */
export enum FeatureFlags {
  SHOW_ACTIVITY = 'showActivity',
  SHOW_ADDRESS = 'showAddress',
  /** Shows the Advanced Search modal for building complex filters. */
  SHOW_ADVANCED_SEARCH = 'showAdvancedSearch',
  SHOW_CONTACT = 'showContact',
  SHOW_CRM_BOT = 'showCrmBot',
  SHOW_DASHBOARD = 'showDashboard',
  SHOW_DOCUMENT = 'showDocument',
  SHOW_GLOBAL_SEARCH = 'showGlobalSearch',
  SHOW_INVOICE = 'showInvoice',
  SHOW_LEAD = 'showLead',
  SHOW_LIST = 'showList',
  SHOW_MESSAGE = 'showMessage',
  /** Shows "My Work" entries (e.g., My Activities, My Contacts) in the sidebar if present in the featureFlags array. */
  SHOW_MY_WORK = 'showMyWork',
  SHOW_OPPORTUNITY = 'showOpportunity',
  SHOW_ORDER = 'showOrder',
  SHOW_QUOTE = 'showQuote',
  SHOW_SEGMENT_FILTER = 'showSegmentFilter',
  HIDE_REGION = 'hideRegion',
  SHOW_TICKET = 'showTicket',
  SHOW_TIME_TRACKING = 'showTimeTracking',
}

export type FeatureFlag = (typeof FeatureFlags)[keyof typeof FeatureFlags]

export enum NeoDXPInstanceInfo {
  ID = '_id',
  TITLE = 'title',
  HIDE_PISA_SALES_DEEP_LINKS = 'hidePisaSalesDeepLinks',
  FEATURE_FLAGS = 'featureFlags',
}

export const ACTIVITY_ASC_ORDER: DataScopeOrder = ['activity_beginAt', 'asc']
export const ACTIVITY_DESC_ORDER: DataScopeOrder = ['activity_beginAt', 'desc']

export const ACTIVITY_TAB_ASC_ORDER: DataScopeOrder = [
  'activity_updatedAt',
  'asc',
]
export const ACTIVITY_TAB_DESC_ORDER: DataScopeOrder = [
  'activity_updatedAt',
  'desc',
]

export const ASC_ORDER: DataScopeOrder = ['createdAt', 'asc']
export const DESC_ORDER: DataScopeOrder = ['createdAt', 'desc']

export const CONTACT_ASC_ORDER: DataScopeOrder = ['createdAt', 'asc']
export const CONTACT_DESC_ORDER: DataScopeOrder = ['createdAt', 'desc']

export const DOCUMENT_ASC_ORDER: DataScopeOrder = ['createdAt', 'asc']
export const DOCUMENT_DESC_ORDER: DataScopeOrder = ['createdAt', 'desc']

export const INVOICE_ASC_ORDER: DataScopeOrder = ['invoiceAt', 'asc']
export const INVOICE_DESC_ORDER: DataScopeOrder = ['invoiceAt', 'desc']

export const PROJECT_ASC_ORDER: DataScopeOrder = ['project_createdAt', 'asc']
export const PROJECT_DESC_ORDER: DataScopeOrder = ['project_createdAt', 'desc']
